// LineMidSegmentHandleController (PRD-F5, orthogonal Stage 2 — increment 1).
//
// The first step toward Miro-style draggable elbow segments: an orthogonal
// line drawn as a "Z" (start segment, middle segment, end segment) gets ONE
// draggable handle on its MIDDLE segment. Sliding it moves the bend between the
// endpoints, so the two outer segments change length. The middle segment stays
// axis-locked (a vertical middle slides horizontally, a horizontal middle
// slides vertically) and clamped between the endpoints, so the bounding box
// never changes and the path stays perfectly orthogonal.
//
// Live preview repaints via `renderLine` on every frame; the single
// `SetLineMidRatioCommand` on `dragend` is the only undo step. Structure mirrors
// `LineEndpointHandlesController` (FC17 anti-race + follow-the-line resyncs).
//
// A later increment will make the OUTER segments draggable too (inserting new
// bends); that needs the richer waypoint model and is intentionally out of
// scope here.

import Konva from 'konva';
import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import type { History } from '@o2d/history';

import type { BoardState } from '../../core/state/board-state.js';
import type { WhiteboardBus } from '../../core/bus/whiteboard-bus.js';
import { createSelectionHandle, sizeSelectionHandle } from '../../core/konva/selection-handle.js';
import { isLine } from './line.types.js';
import type { LineObject } from './line.interface.js';
import { SetLineMidRatioCommand } from './line.commands.js';
import { wireHandleHover } from './line-handle-hover.js';
import { computeLineWorldPoints } from './line.renderer.js';
import type { LinePathStrategyRegistry } from './strategies/line-path-strategy.interface.js';

const MID_HANDLE_NAME = 'line-mid-handle';

/** Geometry of the middle segment: where the handle sits and how it slides. */
interface MidSegment {
  /** Handle position (world) = midpoint of the middle segment. */
  readonly cx: number;
  readonly cy: number;
  /** Axis the handle slides along. */
  readonly axis: 'x' | 'y';
  /** World start/end coordinate on the sliding axis (endpoints of the line). */
  readonly from: number;
  readonly to: number;
}

export interface LineMidSegmentHandleDeps {
  overlayLayer(): Konva.Layer | undefined;
  selectionLayer(): Konva.Layer | undefined;
  konvaNodeFor(id: string): Konva.Node | undefined;
  getReadonly(): boolean;
  pathRegistry(): LinePathStrategyRegistry | undefined;
  /** Repaint a line from a transient object WITHOUT touching BoardState. */
  renderLine(line: LineObject): void;
}

export class LineMidSegmentHandleController implements ReactiveController {
  readonly #bus: WhiteboardBus;
  readonly #state: BoardState;
  readonly #history: History<BoardState>;
  readonly #deps: LineMidSegmentHandleDeps;
  readonly #disconnect$ = new Subject<void>();
  #handle: Konva.Rect | null = null;
  /**
   * The single selected line's id, or null. Tracked independently of `#handle`
   * so a line that only BECOMES a draggable Z later (e.g. the user toggles it
   * to orthogonal while it is selected) still grows a handle on the next
   * `object-updated`.
   */
  #selectedLineId: string | null = null;
  #dragging = false;
  /** midRatio at dragstart, restored by undo. */
  #fromRatio: number | undefined = undefined;
  #stateTeardown: Array<() => void> = [];

  constructor(
    host: ReactiveControllerHost,
    bus: WhiteboardBus,
    state: BoardState,
    history: History<BoardState>,
    deps: LineMidSegmentHandleDeps,
  ) {
    this.#bus = bus;
    this.#state = state;
    this.#history = history;
    this.#deps = deps;
    host.addController(this);
  }

  hostConnected(): void {
    this.#bus.selection$
      .pipe(takeUntil(this.#disconnect$), takeUntil(this.#bus.destroy$))
      .subscribe({ next: (ids) => this.#onSelectionChanged(ids) });

    this.#bus.liveTransform$
      .pipe(takeUntil(this.#disconnect$), takeUntil(this.#bus.destroy$))
      .subscribe({ next: () => this.#refresh('live') });

    this.#stateTeardown = [
      this.#state.on('object-updated', ({ object }) => {
        if (object.id === this.#selectedLineId) this.#refresh('state');
      }),
      this.#state.on('viewport-changed', () => this.#refresh('state')),
    ];
  }

  hostDisconnected(): void {
    this.#selectedLineId = null;
    this.#destroyHandle();
    for (const off of this.#stateTeardown.splice(0)) off();
    this.#disconnect$.next();
    this.#disconnect$.complete();
  }

  #layer(): Konva.Layer | undefined {
    return this.#deps.overlayLayer() ?? this.#deps.selectionLayer();
  }

  /**
   * The middle segment of an orthogonal "Z", or null when the selected line
   * has none — a straight line, a single-bend "L" (mixed docking), or a
   * degenerate/axis-aligned route. `origin` overrides the group position for
   * the live-drag case where the Konva node has moved but state has not.
   */
  #midSegment(obj: LineObject, origin?: { x: number; y: number }): MidSegment | null {
    const registry = this.#deps.pathRegistry();
    if (!registry) return null;
    const src = origin ? { ...obj, x: origin.x, y: origin.y } : obj;
    const pts = computeLineWorldPoints(src, registry);
    // A genuine Z has exactly four vertices (8 numbers): start, two bends, end.
    if (pts.length !== 8) return null;
    const [sx, sy, b1x, b1y, b2x, b2y, ex, ey] = pts as [
      number, number, number, number, number, number, number, number,
    ];
    const cx = (b1x + b2x) / 2;
    const cy = (b1y + b2y) / 2;
    if (b1x === b2x) {
      // Vertical middle segment (HVH) → slides horizontally between sx and ex.
      return { cx, cy, axis: 'x', from: sx, to: ex };
    }
    if (b1y === b2y) {
      // Horizontal middle segment (VHV) → slides vertically between sy and ey.
      return { cx, cy, axis: 'y', from: sy, to: ey };
    }
    return null;
  }

  #onSelectionChanged(ids: ReadonlyArray<string>): void {
    // Which single line (if any) is selected — regardless of whether it
    // currently qualifies for a handle. Readonly boards never get one.
    let lineId: string | null = null;
    if (ids.length === 1 && !this.#deps.getReadonly()) {
      const obj = this.#state.getObject(ids[0]!);
      if (obj && isLine(obj)) lineId = ids[0]!;
    }
    // A redundant re-emit of the same line must not tear down a handle the
    // pointer is currently holding (see endpoint controller for the rationale).
    if (lineId === this.#selectedLineId && this.#handle) {
      this.#refresh('state');
      return;
    }
    this.#destroyHandle();
    this.#selectedLineId = lineId;
    this.#refresh('state');
  }

  /**
   * Reconcile the handle with the current state: create it when the selected
   * line is a draggable orthogonal "Z", reposition it when one already exists,
   * and remove it when the line no longer qualifies. Called on selection,
   * committed updates (incl. the straight↔orthogonal toggle), live drags and
   * zoom — so the handle appears the moment the line becomes an elbow.
   */
  #refresh(source: 'state' | 'live'): void {
    if (this.#dragging) return;
    if (this.#selectedLineId === null) {
      this.#destroyHandle();
      return;
    }
    const obj = this.#state.getObject(this.#selectedLineId);
    if (!obj || !isLine(obj)) {
      this.#destroyHandle();
      return;
    }
    const node = source === 'live' ? this.#deps.konvaNodeFor(this.#selectedLineId) : undefined;
    const origin = node ? { x: node.x(), y: node.y() } : undefined;
    const seg = this.#midSegment(obj, origin);
    if (!seg) {
      this.#destroyHandle();
      return;
    }
    if (this.#handle) this.#repositionHandle(seg);
    else this.#createHandle(this.#selectedLineId, seg);
  }

  #repositionHandle(seg: MidSegment): void {
    if (!this.#handle) return;
    const scale = this.#state.viewport.scale || 1;
    this.#handle.position({ x: seg.cx, y: seg.cy });
    sizeSelectionHandle(this.#handle, scale);
    this.#layer()?.batchDraw();
  }

  #createHandle(lineId: string, seg: MidSegment): void {
    const layer = this.#layer();
    if (!layer) return;
    const scale = this.#state.viewport.scale || 1;
    const handle = createSelectionHandle({ x: seg.cx, y: seg.cy, scale, name: MID_HANDLE_NAME });
    this.#wireDrag(handle, lineId);
    wireHandleHover(handle, () => {
      const o = this.#state.getObject(lineId);
      return o && isLine(o) ? o.strokeColor : undefined;
    });
    layer.add(handle);
    this.#handle = handle;
    layer.batchDraw();
  }

  #wireDrag(handle: Konva.Rect, lineId: string): void {
    // Swallow a bare click so a press that never becomes a drag cannot bubble
    // to the stage and clear the selection (which would destroy this handle).
    handle.on('click tap', (e: Konva.KonvaEventObject<MouseEvent>) => {
      e.cancelBubble = true;
    });

    handle.on('dragstart', () => {
      this.#dragging = true;
      const obj = this.#state.getObject(lineId);
      this.#fromRatio = obj && isLine(obj) ? obj.midRatio : undefined;
    });

    handle.on('dragmove', () => {
      const obj = this.#state.getObject(lineId);
      if (!obj || !isLine(obj)) return;
      const seg = this.#midSegment(obj);
      if (!seg) return;
      const ratio = this.#applyHandleConstraint(handle, seg);
      this.#deps.renderLine({ ...obj, midRatio: ratio });
    });

    handle.on('dragend', () => {
      const obj = this.#state.getObject(lineId);
      this.#dragging = false;
      if (!obj || !isLine(obj)) {
        this.#fromRatio = undefined;
        return;
      }
      const seg = this.#midSegment(obj);
      const to = seg ? this.#ratioFromHandle(handle, seg) : obj.midRatio;
      const from = this.#fromRatio;
      this.#fromRatio = undefined;
      // `undefined` and 0.5 are the same bend (midpoint) — a click that never
      // moved the handle must not push an undo step.
      if ((to ?? 0.5) === (from ?? 0.5)) {
        this.#refresh('state');
        return;
      }
      this.#history.push(new SetLineMidRatioCommand(lineId, from, to), this.#state);
      this.#refresh('state');
    });
  }

  /**
   * Lock the handle to its segment's sliding axis, clamp it between the
   * endpoints, snap it back onto the segment, and return the resulting ratio.
   */
  #applyHandleConstraint(handle: Konva.Rect, seg: MidSegment): number {
    const ratio = this.#ratioFromHandle(handle, seg);
    const lo = Math.min(seg.from, seg.to);
    const hi = Math.max(seg.from, seg.to);
    if (seg.axis === 'x') {
      handle.x(Math.max(lo, Math.min(hi, seg.from + ratio * (seg.to - seg.from))));
      handle.y(seg.cy);
    } else {
      handle.x(seg.cx);
      handle.y(Math.max(lo, Math.min(hi, seg.from + ratio * (seg.to - seg.from))));
    }
    return ratio;
  }

  /** Fraction in [0,1] of the handle's position along the segment's axis. */
  #ratioFromHandle(handle: Konva.Rect, seg: MidSegment): number {
    const span = seg.to - seg.from;
    if (span === 0) return 0.5;
    const pos = seg.axis === 'x' ? handle.x() : handle.y();
    const t = (pos - seg.from) / span;
    return t < 0 ? 0 : t > 1 ? 1 : t;
  }

  /** Destroy the handle (if any). Leaves `#selectedLineId` untouched so a
   * later `#refresh` can recreate it once the line qualifies again. */
  #destroyHandle(): void {
    this.#dragging = false;
    this.#fromRatio = undefined;
    if (!this.#handle) return;
    this.#handle.destroy();
    this.#handle = null;
    this.#layer()?.batchDraw();
  }
}

export const LINE_MID_HANDLE_NAME = MID_HANDLE_NAME;
